import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { LazySftpLogsGrid } from '@/components/lazy';
import { withLazyLoading } from '@/components/LazyComponent';

const SftpLogsGrid = withLazyLoading(LazySftpLogsGrid);

export const SftpLogsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed' | 'indeterminate'>('all');

  // Check for entity parameter in URL on mount
  useEffect(() => {
    const entityParam = searchParams.get('entity');
    if (entityParam) {
      setEntityFilter(entityParam);
    }
  }, [searchParams]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEntityFilter('all');
    setStatusFilter('all');
  };

  const activeFiltersCount = [
    searchTerm,
    entityFilter !== 'all' ? entityFilter : '',
    statusFilter !== 'all' ? statusFilter : '',
  ].filter(Boolean).length;

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          Home
        </Link>
        <Link underline="hover" color="inherit" href="/sftp-sync">
          SFTP Sync
        </Link>
        <Typography color="text.primary">Logs</Typography>
      </Breadcrumbs>

      <Paper sx={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
        {/* Ribbon toolbar with filters */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 80,
            px: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            flexWrap: 'nowrap',
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            placeholder="Search log messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={entityFilter}
              label="Entity"
              onChange={(e) => setEntityFilter(e.target.value)}
            >
              <MenuItem value="all">&lt;&lt;All Entities&gt;&gt;</MenuItem>
              <MenuItem value="UNKNOWN">Unknown</MenuItem>
              <MenuItem value="ITEM">Item</MenuItem>
              <MenuItem value="CUSTOMER">Customer</MenuItem>
              <MenuItem value="SALES_PEOPLE">Sales People</MenuItem>
              <MenuItem value="ORDER">Order</MenuItem>
              <MenuItem value="CONFIRMATION_EMAIL">Confirmation Email</MenuItem>
              <MenuItem value="ITEM_CLEANER">Item Cleaner</MenuItem>
              <MenuItem value="CUSTOMER_CLEANER">Customer Cleaner</MenuItem>
              <MenuItem value="ITEM_PRICING">Item Pricing</MenuItem>
              <MenuItem value="LOCATION">Location</MenuItem>
              <MenuItem value="JOB">Job</MenuItem>
              <MenuItem value="QUICK_BOOKS">QuickBooks</MenuItem>
              <MenuItem value="WALMART_OPD_ZERO">Walmart OPD Zero</MenuItem>
              <MenuItem value="SFTP_SYNCHRONIZATION">SFTP Synchronization</MenuItem>
              <MenuItem value="ON_HAND_PROCESSING">On Hand Processing</MenuItem>
              <MenuItem value="ON_HAND_QUEUING">On Hand Queuing</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'failed' | 'indeterminate')}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="indeterminate">Indeterminate</MenuItem>
            </Select>
          </FormControl>

          {activeFiltersCount > 0 && (
            <Chip
              label={`${activeFiltersCount} active filter${activeFiltersCount !== 1 ? 's' : ''}`}
              size="small"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        {/* Grid */}
        <Box sx={{ flex: 1 }}>
          <SftpLogsGrid
            height="100%"
            searchTerm={searchTerm}
            entityFilter={entityFilter}
            statusFilter={statusFilter}
          />
        </Box>
      </Paper>
    </Box>
  );
};